/**
 * MaintenanceBlade — slide-out add/complete surface for the site dashboard.
 *
 * A single-entry blade: either the inline "add entry" form or a focused view
 * of one entry with inline resolution. The full Open / Resolved list lives at
 * the routed Maintenance History page, not in the blade; a footer link jumps
 * there.
 *
 * Reads maintenance logs from the odl-app maintenanceLogs slice (lazy-loaded on
 * first open) to resolve the focused entry. Writes via the odl-app maintenance
 * endpoints; add/resolve are permission-gated via capabilities.
 *
 * Contains:
 * - MaintenanceBlade — DashboardBlade drawer (default export)
 */
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectCapabilities } from '@sophocles/features/capabilities';
import {
  loadMaintenanceLogs,
  prependEntry,
  patchEntry,
  selectMaintenanceLogs,
  selectMaintenanceLogsStatus,
} from '@sophocles/features/entities/site';
import DashboardBlade from '@sophocles/views/sites/components/SiteDashboard/DashboardBlade';
import MaintenanceAddForm from '@sophocles/views/sites/components/SiteDashboard/maintenance/MaintenanceAddForm';
import MaintenanceEntryRow from '@sophocles/views/sites/components/SiteDashboard/maintenance/MaintenanceEntryRow';

const BLADE_WIDTH = { xs: '100%', sm: 560 };

/**
 * @param {object}        props
 * @param {boolean}       props.open             - controls drawer visibility
 * @param {Function}      props.onClose          - called when the drawer should close
 * @param {number|string} props.siteId           - the site id
 * @param {string}        props.maintenancePath  - router path for the "Full history" footer link
 * @param {number}        props.startAddingToken - changes when an external Add action should open the add form
 * @param {number|string} props.focusedLogId     - when set, show the single focused entry
 */
export default function MaintenanceBlade({ open, onClose, siteId, maintenancePath, startAddingToken, focusedLogId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const capabilities = useSelector(selectCapabilities);
  const canEdit = capabilities?.can_edit_maintenance ?? false;

  const logs = useSelector(selectMaintenanceLogs(siteId));
  const { loading } = useSelector(selectMaintenanceLogsStatus(siteId));

  const [adding, setAdding] = useState(false);

  // Lazy-load on first open.
  useEffect(() => {
    if (open && siteId) {
      dispatch(loadMaintenanceLogs(siteId));
    }
  }, [open, siteId, dispatch]);

  useEffect(() => {
    if (startAddingToken == null) return;
    setAdding(true);
  }, [startAddingToken]);

  // Exit add mode when a focused entry is requested from outside.
  useEffect(() => {
    if (focusedLogId != null) setAdding(false);
  }, [focusedLogId]);

  const focusedLog = focusedLogId != null ? (logs.find(l => l.id === focusedLogId) ?? null) : null;

  const isFocused = focusedLog != null;
  const isAddOnly = adding && !isFocused;
  const isLoadingFocus = focusedLogId != null && focusedLog == null && loading;

  const handleViewHistory = () => {
    onClose?.();
    navigate(maintenancePath);
  };

  const footer = (
    <Typography
      component="span"
      variant="body2"
      onClick={handleViewHistory}
      sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
    >
      {t('maintenance.viewHistory', 'View full history')}
    </Typography>
  );

  return (
    <DashboardBlade open={open} onClose={onClose} width={BLADE_WIDTH} title={t('sites.maintenanceLog')} footer={footer}>
      {isAddOnly ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 'var(--page-gap)' }}>
          <MaintenanceAddForm
            siteId={siteId}
            onDone={newEntry => {
              setAdding(false);
              if (newEntry) dispatch(prependEntry({ siteId, entry: newEntry }));
              onClose?.();
            }}
          />
        </Box>
      ) : isFocused ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 'var(--page-gap)' }}>
          <MaintenanceEntryRow
            log={focusedLog}
            siteId={siteId}
            canEdit={canEdit}
            defaultResolving={canEdit && focusedLog.date_resolved == null}
            onPatched={patch => dispatch(patchEntry({ siteId, id: focusedLog.id, patch }))}
          />
        </Box>
      ) : isLoadingFocus ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 'var(--page-gap)' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
            {t('general.loading', 'Loading...')}
          </Typography>
        </Box>
      ) : null}
    </DashboardBlade>
  );
}

MaintenanceBlade.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maintenancePath: PropTypes.string,
  startAddingToken: PropTypes.number,
  focusedLogId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
